import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthContextType } from '../types';
import { setAuthHeader, clearAuthHeader, login as apiLogin, healthCheck } from '../services/api';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('authToken');
    if (token) {
      setAuthHeader(token);
      // Verify token by making a request
      healthCheck().then(() => {
        setIsAuthenticated(true);
      }).catch(() => {
        clearAuthHeader();
        setIsAuthenticated(false);
      });
    }
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    try {
      const response = await apiLogin(username, password);
      setAuthHeader(response.token);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      clearAuthHeader();
      setIsAuthenticated(false);
      return false;
    }
  };

  const logout = () => {
    clearAuthHeader();
    setIsAuthenticated(false);
  };

  const value: AuthContextType = {
    isAuthenticated,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};